import * as fs from "node:fs";
import { spawnSync } from "node:child_process";
import type { TokenCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";
import { getAgentVersion } from "./foundry-agent-get.js";
import { disableA2a } from "./a2a-enable.js";
import { unlinkBrain } from "./brain-unlink.js";
import { deleteAgent } from "./foundry-agents.js";
import { agentYamlPath, readAgentYaml } from "./agent-yaml.js";
import { apiCall, type ApiClientOptions } from "./api-client.js";
import { resolveFoundryProject, type FoundryProject } from "./foundry-project.js";

// ──── public types ─────────────────────────────────────────────────────────────

export interface RemovePlan {
  agentName: string;
  agentExists: boolean;
  kind?: "prompt" | "hosted";
  hasBrain: boolean;
  hasA2a: boolean;
  brainRepo?: string;
  bindings: { channel: string; bindingId: string }[];
}

export interface RemoveOpts {
  keepBindings: boolean;
  keepA2a: boolean;
  deleteBrainRepo: boolean;
}

export interface StepResult {
  name: string;
  status: "ok" | "skipped" | "failed";
  detail?: string;
}

export interface RemoveAgentDeps {
  getAgent: (agentName: string) => Promise<{
    exists: boolean;
    kind?: "prompt" | "hosted";
    hasBrain: boolean;
    hasA2a: boolean;
    brainRepo?: string;
  }>;
  listAgentBindings: (agentName: string) => Promise<{ channel: string; bindingId: string }[]>;
  deleteBinding: (bindingId: string) => Promise<void>;
  disableA2a: (agentName: string) => Promise<void>;
  unlinkBrain: (agentName: string) => Promise<void>;
  deleteAgent: (agentName: string) => Promise<void>;
  deleteBrainRepo: (repo: string) => Promise<void>;
  removeLocalYaml: (agentName: string) => void;
  onProgress?: (m: string) => void;
}

// ──── plan phase (read-only) ───────────────────────────────────────────────────

/**
 * Build a plan describing what will be removed. No mutations.
 * Bindings are always listed (they can outlive the agent).
 * When the agent is not found, hasBrain/hasA2a are set to false.
 */
export async function planAgentRemoval(
  agentName: string,
  deps: RemoveAgentDeps,
): Promise<RemovePlan> {
  const [agent, bindings] = await Promise.all([
    deps.getAgent(agentName),
    deps.listAgentBindings(agentName),
  ]);

  return {
    agentName,
    agentExists: agent.exists,
    kind: agent.kind,
    hasBrain: agent.hasBrain,
    hasA2a: agent.hasA2a,
    brainRepo: agent.brainRepo,
    bindings,
  };
}

// ──── execute phase (mutating cascade) ────────────────────────────────────────

/**
 * Execute the removal cascade in order: bindings → a2a → brain (+repo) → agent → local yaml.
 * Each step is wrapped in its own try/catch so one failure does not abort the rest.
 * Returns the results of every step.
 */
export async function executeAgentRemoval(
  plan: RemovePlan,
  opts: RemoveOpts,
  deps: RemoveAgentDeps,
): Promise<{ steps: StepResult[] }> {
  const steps: StepResult[] = [];
  const progress = deps.onProgress ?? ((_: string) => { /* no-op */ });

  // ── bindings ─────────────────────────────────────────────────────────────────
  if (opts.keepBindings) {
    steps.push({ name: "bindings", status: "skipped", detail: "--keep-bindings" });
  } else if (plan.bindings.length === 0) {
    steps.push({ name: "bindings", status: "skipped", detail: "no bindings" });
  } else {
    for (const b of plan.bindings) {
      const stepName = `binding:${b.channel}:${b.bindingId}`;
      try {
        progress(`Deleting binding ${b.bindingId} (${b.channel})…`);
        await deps.deleteBinding(b.bindingId);
        steps.push({ name: stepName, status: "ok" });
      } catch (e) {
        steps.push({ name: stepName, status: "failed", detail: e instanceof Error ? e.message : String(e) });
      }
    }
  }

  // ── a2a connection ────────────────────────────────────────────────────────────
  if (!plan.hasA2a || opts.keepA2a) {
    steps.push({ name: "a2a", status: "skipped", detail: !plan.hasA2a ? "no a2a" : "--keep-a2a" });
  } else if (!plan.agentExists) {
    steps.push({ name: "a2a", status: "skipped", detail: "agent not found" });
  } else if (plan.kind === "hosted") {
    // Hosted a2a is metadata-only — no Foundry connection to delete.
    // The metadata is removed when the agent itself is deleted.
    steps.push({ name: "a2a", status: "skipped", detail: "hosted: a2a is metadata-only, removed with the agent" });
  } else {
    try {
      progress("Disabling a2a…");
      await deps.disableA2a(plan.agentName);
      steps.push({ name: "a2a", status: "ok" });
    } catch (e) {
      steps.push({ name: "a2a", status: "failed", detail: e instanceof Error ? e.message : String(e) });
    }
  }

  // ── brain connection ──────────────────────────────────────────────────────────
  if (!plan.hasBrain) {
    steps.push({ name: "brain", status: "skipped", detail: "no brain" });
  } else if (!plan.agentExists) {
    steps.push({ name: "brain", status: "skipped", detail: "agent not found" });
  } else if (plan.kind === "hosted") {
    // Hosted brain is in-container — no Foundry connection to delete.
    // The in-container credential dies with the agent; the GitHub App is org-wide
    // and must not be uninstalled here.
    steps.push({ name: "brain", status: "skipped", detail: "hosted: in-container brain removed with the agent" });

    // The GitHub repo, however, persists and can be explicitly cleaned up.
    if (opts.deleteBrainRepo && plan.brainRepo) {
      try {
        progress(`Deleting brain repo ${plan.brainRepo}…`);
        await deps.deleteBrainRepo(plan.brainRepo);
        steps.push({ name: "brain-repo", status: "ok" });
      } catch (e) {
        steps.push({ name: "brain-repo", status: "failed", detail: e instanceof Error ? e.message : String(e) });
      }
    }
  } else {
    let brainUnlinked = false;
    try {
      progress("Unlinking brain…");
      await deps.unlinkBrain(plan.agentName);
      steps.push({ name: "brain", status: "ok" });
      brainUnlinked = true;
    } catch (e) {
      steps.push({ name: "brain", status: "failed", detail: e instanceof Error ? e.message : String(e) });
    }

    // brain-repo deletion is separate so it can fail independently,
    // but ONLY attempted when the unlink succeeded.
    if (opts.deleteBrainRepo && plan.brainRepo && brainUnlinked) {
      try {
        progress(`Deleting brain repo ${plan.brainRepo}…`);
        await deps.deleteBrainRepo(plan.brainRepo);
        steps.push({ name: "brain-repo", status: "ok" });
      } catch (e) {
        steps.push({ name: "brain-repo", status: "failed", detail: e instanceof Error ? e.message : String(e) });
      }
    }
  }

  // ── agent ─────────────────────────────────────────────────────────────────────
  if (!plan.agentExists) {
    steps.push({ name: "agent", status: "skipped", detail: "already gone" });
  } else {
    try {
      progress(`Deleting agent ${plan.agentName}…`);
      await deps.deleteAgent(plan.agentName);
      steps.push({ name: "agent", status: "ok" });
    } catch (e) {
      steps.push({ name: "agent", status: "failed", detail: e instanceof Error ? e.message : String(e) });
    }
  }

  // ── local yaml (always, idempotent) ──────────────────────────────────────────
  try {
    deps.removeLocalYaml(plan.agentName);
    steps.push({ name: "yaml", status: "ok" });
  } catch (e) {
    steps.push({ name: "yaml", status: "failed", detail: e instanceof Error ? e.message : String(e) });
  }

  return { steps };
}

// ──── gateway bindings shape ──────────────────────────────────────────────────

interface BindingRecord {
  bindingId: string;
  channel: string;
  agentName: string;
  status: string;
}

interface BindingListResponse { bindings: BindingRecord[] }

// ──── default deps factory ────────────────────────────────────────────────────

export interface DefaultRemoveAgentDepsArgs {
  credential: TokenCredential;
  project: FoundryProject;
  /** Called lazily — only when unlinkBrain actually runs. Allows no-brain removals
   *  to skip the Key Vault URI check entirely. */
  resolveKvUri: () => string;
  gatewayCtx: ApiClientOptions;
  home?: string;
  onProgress?: (m: string) => void;
}

export function defaultRemoveAgentDeps(args: DefaultRemoveAgentDepsArgs): RemoveAgentDeps {
  const { credential, project, resolveKvUri, gatewayCtx, home, onProgress } = args;
  const projectEndpoint = project.endpoint;
  const projectArmId = `${project.accountScope}/projects/${project.projectName}`;

  return {
    onProgress,

    async getAgent(agentName: string) {
      try {
        const rec = await getAgentVersion({ credential, projectEndpoint, agentName });
        const meta = rec.metadata ?? {};
        const hasBrain = !!(meta.brain && meta.brain !== "");
        const hasA2a = meta.a2a === "true";
        let brainRepo: string | undefined;
        if (hasBrain) {
          try {
            brainRepo = (JSON.parse(meta.brain) as { repo?: string }).repo;
          } catch {
            // malformed brain meta — treat as no repo
          }
        }
        return {
          exists: true,
          kind: rec.definition.kind,
          hasBrain,
          hasA2a,
          brainRepo,
        };
      } catch (e) {
        if (e instanceof LocalCliError && e.code === "AGENT_NOT_FOUND") {
          return { exists: false, hasBrain: false, hasA2a: false };
        }
        throw e;
      }
    },

    async listAgentBindings(agentName: string) {
      const data = await apiCall<BindingListResponse>(gatewayCtx, { method: "GET", path: "/api/bindings" });
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `data` is unvalidated network JSON (apiCall validates only the envelope, not the inner shape); a partial/version-skewed gateway response may omit `bindings`
      return (data.bindings ?? [])
        .filter((b) => b.agentName === agentName)
        .map((b) => ({ channel: b.channel, bindingId: b.bindingId }));
    },

    async deleteBinding(bindingId: string) {
      await apiCall<unknown>(gatewayCtx, {
        method: "DELETE",
        path: `/api/bindings/${encodeURIComponent(bindingId)}`,
      });
    },

    async disableA2a(agentName: string) {
      await disableA2a({
        credential,
        projectEndpoint,
        projectArmId,
        agentName,
        onProgress,
      });
    },

    async unlinkBrain(agentName: string) {
      await unlinkBrain({
        credential,
        kvUri: resolveKvUri(),
        projectEndpoint,
        projectArmId,
        agentName,
        keepAppInstall: true,
        home,
        onProgress,
      });
    },

    async deleteAgent(agentName: string) {
      await deleteAgent({ credential, endpoint: projectEndpoint, name: agentName });
    },

    deleteBrainRepo(repo: string): Promise<void> {
      const result = spawnSync("gh", ["repo", "delete", repo, "--yes"], { encoding: "utf8" });
      if (result.status !== 0) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- spawnSync returns stderr:null when the spawn itself fails (e.g. gh not on PATH); @types/node types it non-null
        return Promise.reject(new Error(result.stderr?.trim() || `gh repo delete exited with code ${String(result.status)}`));
      }
      return Promise.resolve();
    },

    removeLocalYaml(agentName: string) {
      const p = agentYamlPath(agentName, home);
      if (fs.existsSync(p)) fs.rmSync(p);
    },
  };
}

// Re-export resolveFoundryProject for consumers that need it
export { resolveFoundryProject };

// Re-export readAgentYaml for the command
export { readAgentYaml };
